import React, { useEffect, useRef, useState } from "react";
import SetPageTitle from "../../../components/SetPageTitle";
import SmallCards from "../../../components/dashboard/SmallCards";
import {
  Badge,
  Button,
  Card,
  Form,
  OverlayTrigger,
  Row,
  Col,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import {
  formatNumberToLocal,
  sortProductionMonthaData,
  selectColourStyles2,
} from "../../../helpers";
import wallet from "../../../assets/images/wallet.webp";
import { useTranslation } from "react-i18next";
import network from "../../../assets/images/network.webp";
import "react-datepicker/dist/react-datepicker.css";
import {
  useGetEwalletBalanceQuery,
  useGetEwalletDataQuery,
  useGetProductionMonthListQuery,
} from "../../../app/features/apiSlice";
import Loader from "../../../components/Loader";
import Error from "../../../components/Error";
import moment from "moment";
import "moment/locale/it";
import { useParams } from "react-router";
import RequestPayout from "../../../components/payout/RequestPayout";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Select from "react-select";
import { isWithinInterval, parseISO } from "date-fns";
import { skipToken } from "@reduxjs/toolkit/query";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import SelectInput from "../../../components/SelectInput";

const compactSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "31px",
    height: "31px",
    padding: "0 4px",
  }),
  valueContainer: (base) => ({
    ...base,
    height: "31px",
    padding: "0 6px",
  }),
  input: (base) => ({
    ...base,
    margin: "0px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "31px",
  }),
};

const Wallet = () => {
  const { lang } = useParams();
  lang === "it" ? moment.locale("it") : moment.locale("en");
  const { t } = useTranslation();
  const allTimeLabel = t("all");
  const sessionId = useRef(Date.now()).current;
  const [options, setOptions] = useState([]);
  const [selOption, setSelOption] = useState({});
  const [globalFilter, setGlobalFilter] = useState("");
  const [queryParams, setQueryParams] = useState({
    month: "",
    year: "",
    type: "",
    status: "Completed",
    commissionType: "",
    sessionId,
  });
  const [transactionHistory, setTransactionHistory] = useState([]);
  const filter = queryParams.month ? queryParams : skipToken;
  const { data, isLoading, isFetching, isError, error } =
    useGetEwalletDataQuery(filter);
  const { data: eWalletBalance } = useGetEwalletBalanceQuery({ sessionId });
  const productionMonth = useGetProductionMonthListQuery({ sessionId });

  const exportXLSX = () => {
    const filterText = (globalFilter || "").trim().toLowerCase();
    const rows = !filterText
      ? transactionHistory
      : transactionHistory.filter((row) =>
        `${row.createdAt ?? ""} ${row.updatedAt ?? ""} ${row.advisorClient ?? ""} ${row.role ?? ""} ${row.percentage ?? ""} ${row.contractCode ?? ""} ${row.type ?? ""} ${row.status ?? ""} ${row.amount ?? ""}`
          .toLowerCase()
          .includes(filterText),
      );

    if (!rows.length) return alert(t("noRecordsFound"));

    const worksheet = XLSX.utils.aoa_to_sheet([
      [
        t("createdAt"),
        t("updatedAt"),
        t("advisorClient"),
        t("role"),
        t("percentage"),
        t("contract_code"),
        t("paymentType"),
        `${t("amount")} (€)`,
        t("status"),
      ],
      ...rows.map((row) => [
        row.createdAt,
        row.updatedAt,
        row.advisorClient,
        row.role,
        row.percentage,
        row.contractCode,
        t(String(row.type || "")),
        +(row.amount ?? 0),
        t(String(row.status || "")),
      ]),
    ]);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, t("transactions"));
    XLSX.writeFile(workbook, "transaction_wallet.xlsx");
  };

  const handleStatusChange = (e) => {
    const { name, value } = e.target;
    if (name === "type" && value !== "Commission") {
      setQueryParams((prev) => ({
        ...prev,
        [name]: value,
        commissionType: "",
      }));
    } else {
      setQueryParams((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSetDate = (dateString) => {
    if (dateString) {
      const [month, year] = dateString.split(" ");
      setSelOption({
        value: `${month} ${year}`,
        label: `${month} ${year}`,
      });
      setQueryParams((prev) => ({ ...prev, month, year }));
    } else {
      setSelOption({
        value: "",
        label: allTimeLabel,
      });
      setQueryParams((prev) => ({ ...prev, month: "all", year: "all" }));
    }
  };

  useEffect(() => {
    if (
      !isLoading &&
      data?.transactionHistory &&
      data?.transactionHistory.length > 0
    ) {
      const result = [...data?.transactionHistory].map((item) => {
        const createdAt = moment(item?.createdAt).format("DD-MM-YYYY");
        const updatedAt = addedManually(item)
          ? moment(item?.updatedAt).format("DD-MM-YYYY")
          : moment(item?.instalment?.collection_date || item?.updatedAt).format(
            "DD-MM-YYYY",
          );
        const collectionDate = item?.instalment?.collection_date || item?.commission?.ho_plans?.collection_date || "-";
        const advisorClient = addedManually(item)
          ? "Excelior Team"
          : item.commissionFrom
            ? item.commissionFrom
            : "-";
        const role = addedManually(item)
          ? "-"
          : item?.commission?.role
            ? item.commission.role
            : "-";
        const planContractCode = item?.instalment?.plan?.contract_code;
        const hoContractId = item?.hoContractId;
        const contractCode = planContractCode || hoContractId || "-";
        const amount =
          item.type !== "Withdraw"
            ? item.byAdmin && item.amount != null
              ? item.amount
              : item.amount
            : -item.amount;

        return {
          ...item,
          createdAt,
          updatedAt,
          collectionDate,
          advisorClient,
          role,
          contractCode,
          amount,
        };
      });

      setTransactionHistory(result);
    }
  }, [data, isLoading]);

  useEffect(() => {
    if (productionMonth.isSuccess) {
      if (productionMonth.data.length > 0) {
        const sortedData = sortProductionMonthaData(productionMonth.data);
        const currentDate = new Date();

        const currentProductionMonth = sortedData.find((data) => {
          const fromDate = parseISO(data.from_date);
          const toDate = parseISO(data.to_date);
          return isWithinInterval(currentDate, {
            start: fromDate,
            end: toDate,
          });
        });

        const defaultOption = currentProductionMonth || sortedData[0];
        setQueryParams((prev) => ({
          ...prev,
          month: defaultOption.month,
          year: String(defaultOption.year),
        }));
        setSelOption({
          value: `${defaultOption.month} ${defaultOption.year}`,
          label: `${defaultOption.month} ${defaultOption.year}`,
        });
        const optionData = [
          { value: "", label: allTimeLabel },
          ...sortedData.map((data) => ({
            value: `${data.month} ${data.year}`,
            label: `${data.month} ${data.year}`,
          })),
        ];
        setOptions(optionData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionMonth.isSuccess, productionMonth.data]);

  if (isLoading) return <Loader />;
  if (isError) return <Error error={error} />;

  const addedManually = (rowData) => rowData.byAdmin && rowData.amount != null;

  const statusOptions = [
    { value: "Completed", label: t("completed") },
    { value: "Pending", label: t("pending") },
  ];

  const typeOptions = [
    { value: "Commission", label: t("commission") },
    { value: "Bonus", label: t("bonus") },
    { value: "Withdraw", label: t("withdraw") },
  ];

  const commissionTypeOptions = [
    { value: "recurringPayment", label: t("recurringPayment") },
    { value: "docPayment", label: t("docPayment") },
  ];

  return (
    <>
      <SetPageTitle title="sidebar.wallet" display={false} />
      <Row>
        <Col xs="12" md="6" lg="3" className="mb-3 mb-lg-0">
          <SmallCards
            imgSrc={wallet}
            title={t("currentBalance")}
            number={`CHF ${formatNumberToLocal(
              eWalletBalance?.totals?.currentBalanceAvailable || 0
            )}`}
            tooltipText={t("currentBalanceTooltip")}
          />
        </Col>

        <Col xs="12" md="6" lg="3" className="mb-3 mb-lg-0">
          <SmallCards
            icon={<i className="fas fa-calculator"></i>}
            title={t("totalAccumulated")}
            number={`CHF ${formatNumberToLocal(
              eWalletBalance?.totals?.totalAccumulated || 0
            )}`}
            tooltipText={t("totalAccumulatedTooltip")}
          />
        </Col>

        <Col xs="12" md="6" lg="3" className="mb-3 mb-lg-0">
          <SmallCards
            icon={<i className="fas fa-calendar"></i>}
            title={t("productionMonth")}
            number={`${moment(data?.productionMonth?.from).format(
              "Do MMM"
            )} - ${moment(data?.productionMonth?.to).format("Do MMM")}`}
            tooltipText={t("productionMonthTooltip")}
          />
        </Col>

        <Col xs="12" md="6" lg="3" className="mb-3 mb-lg-0">
          <SmallCards
            imgSrc={network}
            title={t("nextPaymentRequestDate")}
            number={`${moment(
              data?.nextPaymentRequestDate?.fromDate
            ).format("Do MMM")} - ${moment(
              data?.nextPaymentRequestDate?.toDate
            ).format("Do MMM")}`}
            tooltipText={t("paymentRequestDateTooltip")}
          />
        </Col>
      </Row>
      <RequestPayout
        balance={`${eWalletBalance?.totals?.currentBalanceAvailable || 0}`}
        recurringPayment={`CHF ${formatNumberToLocal(
          eWalletBalance?.totals?.recurringPending || 0,
        )}`}
      />
      <Card bg="dark1" className="text-light mt-3">
        <Card.Header>{t("transactions")}</Card.Header>
        <Card.Body>
          <div className="row g-2 mb-2 align-items-end">

            {/* Search - mobile full width */}
            <div className="col-12 col-lg-auto">
              <Form.Group className="d-flex flex-column form-calendar">
                <Form.Label>{t("search")}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  size="sm"
                  onInput={(e) => setGlobalFilter(e.target.value)}
                />
              </Form.Group>
            </div>

            {/* Status */}
            <div className="col-6 col-lg-auto">
              <Form.Group className="d-flex flex-column form-calendar">
                <Form.Label>{t("status")}</Form.Label>
                <SelectInput
                  name="status"
                  options={statusOptions}
                  value={queryParams.status}
                  onChange={handleStatusChange}
                  placeholder={t("status")}
                  styleOverrides={compactSelectStyles}
                />
              </Form.Group>
            </div>

            {/* Payment Type */}
            <div className="col-6 col-lg-auto">
              <Form.Group className="d-flex flex-column form-calendar">
                <Form.Label>{t("paymentType")}</Form.Label>
                <SelectInput
                  name="type"
                  options={typeOptions}
                  value={queryParams.type}
                  onChange={handleStatusChange}
                  placeholder={t("paymentType")}
                  styleOverrides={compactSelectStyles}
                />
              </Form.Group>
            </div>

            {/* Commission Type */}
            {queryParams.type === "Commission" && (
              <div className="col-12 col-lg-auto">
                <Form.Group className="d-flex flex-column form-calendar">
                  <Form.Label>{t("commissionType")}</Form.Label>
                  <SelectInput
                    name="commissionType"
                    options={commissionTypeOptions}
                    value={queryParams.commissionType}
                    onChange={handleStatusChange}
                    placeholder={t("all")}
                    styleOverrides={compactSelectStyles}
                  />
                </Form.Group>
              </div>
            )}

            {/* Production Month */}
            <div className="col-12 col-lg-auto">
              <Form.Group className="d-flex flex-column form-calendar">
                <Form.Label>{t("productionMonth")}</Form.Label>

                {productionMonth.isLoading && <Spinner size="sm" />}

                {productionMonth.isSuccess && (
                  <Select
                    options={options}
                    value={selOption}
                    onChange={(e) => handleSetDate(e.value)}
                    placeholder={t("sltLabel")}
                    classNamePrefix="rs"
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: "#fcfcfcff",
                        borderColor: "#3a3a3a",
                        minHeight: "31px",
                        boxShadow: "none",
                        width: "100%",
                        "&:hover": { borderColor: "#3a3a3a" },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "#000000ff",
                        fontSize: "12px",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "#000000ff",
                        fontSize: "12px",
                      }),
                      input: (base) => ({
                        ...base,
                        color: "#000000ff",
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: "#ffffffff",
                        zIndex: 20,
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? "#946D42"
                          : "#ffffffff",
                        color: state.isFocused ? "#fff" : "#000000ff",
                        cursor: "pointer",
                      }),
                      indicatorSeparator: () => ( {
                        display: "none",
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        color: "#946D42",
                        padding: "4px",
                      }),
                    }}
                  />
                )}
              </Form.Group>
            </div>

            {/* Download */}
            <div className="col-12 col-lg-auto d-flex align-items-end">
              <Button
                variant="secondary"
                onClick={exportXLSX}
                data-pr-tooltip="XLSX"
                className="w-100"
              >
                <i className="fa fa-download" />
              </Button>
            </div>

          </div>

          <div style={{ overflowX: "auto" }}>
            <DataTable
              value={transactionHistory}
              paginator
              rows={10}
              size={"small"}
              loading={isLoading || isFetching}
              loadingIcon={<Loader />}
              showGridlines
              stripedRows
              removableSort
              emptyMessage={t("noRecordsFound")}
              globalFilter={globalFilter}
              style={{ minWidth: "900px" }}
            >
              <Column field="createdAt" header={t("createdAt")} sortable />
              <Column field="collectionDate" header={`${t('collection')} ${t('date')}`} sortable body={rowData => rowData?.collectionDate !== '-' ? moment(rowData.collectionDate).format('DD-MM-YYYY') : '-'} />
              <Column field="advisorClient" header={t("advisorClient")} />
              <Column field="role" header={t("role")} />
              <Column field="contractCode" header={t("contract_code")} sortable />
              <Column
                field="type"
                header={t("paymentType")}
                body={(rowData) =>
                  rowData.type !== "Withdraw" ? (
                    t(`${rowData.type}`)
                  ) : (
                    <span className="text-danger">{t(`${rowData.type}`)}</span>
                  )
                }
              />
              <Column field="amount" header={t("amount")} sortable />
              <Column
                field="status"
                header={t("status")}
                body={(rowData) =>
                  rowData.status === "Completed" ? (
                    <Badge bg="success">{t(`${rowData.status}`)}</Badge>
                  ) : (
                    t(`${rowData.status}`)
                  )
                }
              />
              <Column
                body={(rowData) => {
                  if (addedManually(rowData)) {
                    const manualIcon =
                      rowData.type === "Bonus"
                        ? "fa-solid fa-gift"
                        : "fa-solid fa-circle-info";
                    return (
                      <OverlayTrigger
                        placement="left"
                        overlay={
                          <Tooltip id={`plan-tooltip-${rowData?.createdAt}`}>
                            {rowData?.note || ""}
                          </Tooltip>
                        }
                      >
                        <span className="text-light" style={{ cursor: "pointer" }}>
                          <i className={manualIcon}></i>
                        </span>
                      </OverlayTrigger>
                    );
                  }
                  const planContractCode = rowData?.instalment?.plan?.contract_code;
                  const hoContractId = rowData?.hoContractId;
                  const planType =
                    rowData?.instalment?.plan?.planId === 6 ? "plan" : "one";
                  return (
                    <Link
                      to={`/${lang}/plan/excelior-${planType}/${planType === "plan" ? planContractCode : hoContractId
                        }`}
                      className="text-light"
                    >
                      {rowData?.type !== "Withdraw" && (
                        <i className="fa-solid fa-magnifying-glass"></i>
                      )}
                    </Link>
                  );
                }}
              />
            </DataTable>
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

export default Wallet;